

/** loopback + RFC 1918 private ranges + link-local + CGNAT (Tailscale tailnets) */
function isPrivateIpv4(hostname: string): boolean {
    if (/^127\./.test(hostname)) return true;
    if (/^10\./.test(hostname)) return true;
    if (/^192\.168\./.test(hostname)) return true;
    if (/^172\.(1[6-9]|2\d|3[01])\./.test(hostname)) return true;
    if (/^169\.254\./.test(hostname)) return true;
    if (/^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./.test(hostname)) return true;
    return false;
}

/**
 * Whether the url points at a LOCAL development context:
 * - loopback / `localhost`, incl. the reserved `.localhost` TLD (RFC 6761 —
 *   browsers resolve `*.localhost` to loopback; also what Vercel's portless
 *   and similar fixed-local-url tools serve on)
 * - mDNS `.local` names and the reserved `.test` TLD (dnsmasq setups)
 * - private LAN addresses (RFC 1918 / link-local / Tailscale CGNAT) — the
 *   "test on another device via `vite --host`" scenario
 * - loopback-wildcard DNS helpers (lvh.me, localtest.me) and IP-embedding
 *   wildcard DNS (nip.io, sslip.io) when the embedded IP is private
 *
 * Deliberately NOT local: tunnel domains (ngrok, cloudflared, localtunnel…) —
 * those are public urls anyone might visit; treating them as "your own local
 * project" would put the local-dev gates on shared demos. Editing entitlement
 * is a separate policy: see isLicenseRequiredHost for gated tunnel providers.
 */
export function isLocalhostOrIpAddress(url: string | URL): boolean {
    try {
        const urlObj = url instanceof URL ? url : new URL(url);
        // URL.hostname keeps the brackets on IPv6 literals ("[::1]")
        const hostname = urlObj.hostname.toLowerCase().replace(/^\[|\]$/g, "");

        // localhost + the reserved .localhost TLD (web.localhost, app.localhost, …)
        if (hostname === "localhost" || hostname.endsWith(".localhost")) return true;
        // mDNS names (mymachine.local) + the reserved .test TLD
        if (hostname.endsWith(".local") || hostname.endsWith(".test")) return true;

        // wildcard-DNS loopback aliases (always resolve to 127.0.0.1)
        if (/(^|\.)(lvh\.me|localtest\.me)$/.test(hostname)) return true;
        // IP-embedding wildcard DNS: 192.168.1.57.nip.io / 192-168-1-57.sslip.io
        const embedded = /(?:^|\.)(\d{1,3})[.-](\d{1,3})[.-](\d{1,3})[.-](\d{1,3})\.(?:nip\.io|sslip\.io)$/.exec(hostname);
        if (embedded) return isPrivateIpv4(`${embedded[1]}.${embedded[2]}.${embedded[3]}.${embedded[4]}`);

        // IPv6: loopback, unique-local (fc00::/7), link-local (fe80::/10)
        if (hostname === "::1") return true;
        if (/^f[cd][0-9a-f]{2}:/.test(hostname) || hostname.startsWith("fe80:")) return true;

        return isPrivateIpv4(hostname);
    }
    catch (e) {
        return false;
    }
}


/**
 * A url with the QUERY STRING removed and the fragment intact — the sanitizer
 * for any url that leaves the machine as telemetry.
 *
 * Real query strings carry session tokens, signed urls and OAuth codes; a
 * credential landing in the analytics store is an incident, not a privacy
 * preference. The FRAGMENT stays: hash routers keep the app's route there, which
 * is the reason the url is worth reporting at all — and note a `?` inside the
 * fragment is route state, not a query string, so this must go through URL
 * parsing rather than splitting on the first "?".
 *
 * Unparseable input is returned as-is: the caller hands us a raw `location.href`,
 * and dropping the event is worse than reporting a value we couldn't normalize.
 */
export function stripQueryString(url: string): string {
    try {
        const parsed = new URL(url);
        parsed.search = "";
        return parsed.toString();
    }
    catch (e) {
        return url;
    }
}


export type DevelopmentUrlKind = "network" | "cloud" | "file";

/**
 * Cloud contexts where the visitor is almost certainly the AUTHOR of the app
 * being served: AI-builder PREVIEWS and cloud dev environments (forwarded dev
 * servers). Deliberately preview-specific where the platform also hosts
 * PUBLISHED apps (a shipped lovable/replit site is a public site — the gate
 * must not arm on someone's production visitors). Best-effort — these
 * patterns drift as platforms change; keep entries commented.
 */
const CLOUD_DEV_HOST_PATTERNS: RegExp[] = [
    // AI app builders — preview/sandbox domains only
    /(^|\.)lovableproject\.com$/,          // lovable editor sandbox iframes
    /^(id-)?preview--[^.]+\.lovable\.app$/, // lovable previews (published = <name>.lovable.app)
    /(^|\.)vusercontent\.net$/,            // v0.dev previews
    /(^|\.)usercontent\.goog$/,            // Google AI Studio / sandboxed user content previews
    /(^|\.)webcontainer-api\.io$/,         // bolt.new / StackBlitz webcontainers
    /(^|\.)webcontainer\.io$/,
    /(^|\.)csb\.app$/,                     // CodeSandbox previews
    // Cloud dev environments (forwarded dev-server ports)
    /(^|\.)app\.github\.dev$/,             // GitHub Codespaces
    /(^|\.)gitpod\.(io|dev)$/,             // Gitpod workspaces
    /(^|\.)replit\.dev$/,                  // Replit dev urls (published = *.replit.app)
];

/**
 * "Is this someone's own project under development?" — network-local dev
 * (see {@link isLocalhostOrIpAddress}), a `file://` build opened straight off
 * disk, OR a cloud dev/preview context (AI builders like lovable/v0/bolt/AI
 * Studio, cloud IDEs like Codespaces). Use this for author-facing gates (e.g.
 * the local-dev login gate): the person inspecting a preview is almost always
 * its author, while PUBLISHED apps on the same platforms stay "public site".
 */
export function developmentUrlKind(url: string | URL): DevelopmentUrlKind | null {
    if (isLocalhostOrIpAddress(url)) return "network";
    try {
        const urlObj = url instanceof URL ? url : new URL(url);
        // A `file://` page has NO host at all (hostname is "" — even when the
        // authority is spelled "localhost"), so every host test above falls
        // through and this used to read as an ordinary public site. Opening a
        // built index.html off disk is as author-only as it gets: nobody's
        // visitors are on file:///F:/…, so it is its own dev kind.
        if (urlObj.protocol === "file:") return "file";
        const hostname = urlObj.hostname.toLowerCase();
        if (CLOUD_DEV_HOST_PATTERNS.some(pattern => pattern.test(hostname))) return "cloud";
        return null;
    }
    catch (e) {
        return null;
    }
}

export function isDevelopmentUrl(url: string | URL): boolean {
    return developmentUrlKind(url) !== null;
}


/**
 * Registrable domains whose sites are treated as a GATED context even though
 * they are ordinary public hosts — editing there needs an entitlement, the same
 * as someone's own dev server.
 *
 * Why this exists: the dev-context heuristics assume "public host = a visitor
 * playing on someone else's site", which is true for the long tail and false for
 * a company running its own product on a public QA/staging/production host. The
 * observed case: ~28 people across six Anker/eufy hosts over two and a half
 * months, using the resources panel, editing with undo, AI chat and MCP on their
 * 3D pipeline — one login between them, because none of it was gated.
 *
 * Matched on the registrable domain (exact host or any subdomain), NOT a
 * substring: "eufylife.com" must not match "noteufylife.com.evil.example".
 * This explicit list is separate from the staging-hostname rule below.
 * Shared hosting domains such as `*.vercel.app` are not gated wholesale.
 *
 * This list only grows by OBSERVATION. It ships in the bundle today, which means
 * every addition waits for an extension release — moving it behind remote config
 * is the intended next step so it can react to what analytics actually shows.
 */
const LICENSE_REQUIRED_DOMAINS: string[] = [
    "eufylife.com",
    "anker-in.com",
    "anker-launch.com",
    // Shared tunnel domains require an editing entitlement, including shared demos.
    "devtunnels.ms",
    "ngrok-free.app",
    "ngrok-free.dev",
    "ngrok.app",
    "ngrok.dev",
    "ngrok.io",
    "trycloudflare.com",
    "localtunnel.me",
    "loca.lt",
    "localhost.run",
    "lhr.rocks",
    "serveo.net",
    "a.free.pinggy.link",
    // dev/staging hosts only — the production sites stay ungated
    "app.dev.eu.smilecloud.com",
    "staging.samrascenemaker.com",
    "beta-viewer.ikarusdelta.com",
    "beta-app.ikarusdelta.com",
    "builder.peachworlds.com",
    "teams.public.onecdn.static.microsoft",
    "lehub-test.rysaas.cn",
    "anker.mkitreal.com",
    "cdn-staging-eu.seatsio.net",
];

/**
 * First-label naming heuristics: a host whose leading label is one of these is a
 * dev/staging environment on ANY domain (`dev-sandbox.acme.com`), so the visitor
 * is almost certainly the author. Unlike {@link LICENSE_REQUIRED_DOMAINS} (which
 * grows only by observation), this is a pattern — kept narrow on purpose.
 */
const LICENSE_REQUIRED_HOST_LABELS: string[] = [
    "dev-sandbox",
];

/**
 * Whether this host is listed or matches an author/development naming rule.
 */
export function isLicenseRequiredHost(url: string | URL): boolean {
    try {
        const urlObj = url instanceof URL ? url : new URL(url);
        const hostname = urlObj.hostname.toLowerCase().replace(/\.$/, "");
        if (LICENSE_REQUIRED_HOST_LABELS.includes(hostname.split(".")[0])) return true;
        if (hostname.includes("staging")) return true;
        return LICENSE_REQUIRED_DOMAINS.some(domain =>
            hostname === domain || hostname.endsWith(`.${domain}`));
    }
    catch (e) {
        return false;
    }
}
